import React from "react";
import { Transformation } from "../../../../types/functions";
import Resources from "../../../../types/Resources";
import { Modal } from "react-bootstrap";
import LabelCheckbox from "../../../Common/LabelCheckbox";
import HotWorkCategoryTwoOptionsFormState from "../../../../types/forms/Permits/EnergyHarbourPermit/HotWorkCategoryTwoOptionsFormState";
import WarningAlert from "../../../Common/WarningAlert";

type Props = {
    version: number;
    formState: HotWorkCategoryTwoOptionsFormState;
    onChange: (fn: Transformation<HotWorkCategoryTwoOptionsFormState>) => void;
    resources: Resources;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function HotWorkCategoryTwoOptionsModal({
    version,
    resources,
    formState,
    onChange,
    onHide,
    show,
    canManageContent
}: Props) {
    function changeHandler(key: keyof HotWorkCategoryTwoOptionsFormState, value: any) {
        onChange((prevValue) => ({
            ...prevValue,
            [key]: { ...prevValue[key], value }
        }));
    }

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("HotWorkCategoryTwo")}</h1>
            </Modal.Header>
            <Modal.Body>
                <WarningAlert>{resources.get("ToolsRequireHotWorkCertificate")}</WarningAlert>
                {version === 0 ? (
                    <OptionsVersion0
                        formState={formState}
                        resources={resources}
                        changeHandler={changeHandler}
                        canManageContent={canManageContent}
                    />
                ) : (
                    <OptionsVersion2026_03
                        canManageContent={canManageContent}
                        formState={formState}
                        resources={resources}
                        changeHandler={changeHandler}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

type OptionsVersionProps = {
    formState: HotWorkCategoryTwoOptionsFormState;
    changeHandler: (key: keyof HotWorkCategoryTwoOptionsFormState, value: any) => void;
    resources: Resources;
    canManageContent: boolean;
};

function OptionsVersion2026_03({ formState, changeHandler, resources, canManageContent }: OptionsVersionProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.screwDriverDrill.value}
                    id="hot-work-category-two-options__screw-driver-drill"
                    label={resources.get("ScrewDriverDrill")}
                    onChange={(checked) => changeHandler("screwDriverDrill", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.tigersaw.value}
                    id="hot-work-category-two-options__tigersaw"
                    label={resources.get("Tigersaw")}
                    onChange={(checked) => changeHandler("tigersaw", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.circularSaw.value}
                    id="hot-work-category-two-options__circular-saw"
                    label={resources.get("CircularSaw")}
                    onChange={(checked) => changeHandler("circularSaw", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.sparkFreeGrindingWheel.value}
                    id="hot-work-category-two-options__spark-free-grinding-wheel"
                    label={resources.get("SparkFreeGrindingWheel")}
                    onChange={(checked) => changeHandler("sparkFreeGrindingWheel", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.fiberWelding.value}
                    id="hot-work-category-two-options__fiber-welding"
                    label={resources.get("FiberWelding")}
                    onChange={(checked) => changeHandler("fiberWelding", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2 d-flex align-items-center m-2">
                <label className="form-label me-1" htmlFor="hot-work-category-two-options__other">
                    {resources.get("Other")}
                </label>
                <input
                    type="text"
                    className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                    id="hot-work-category-two-options__other"
                    value={formState.other.value ?? ""}
                    onChange={(e) => changeHandler("other", e.target.value)}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function OptionsVersion0({ formState, resources, changeHandler, canManageContent }: OptionsVersionProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.screwDriverDrill.value}
                    id="hot-work-category-two-options__screw-driver-drill"
                    label={resources.get("ScrewDriverDrill")}
                    onChange={(checked) => changeHandler("screwDriverDrill", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.openEquipment.value}
                    id="hot-work-category-two-options__open-equipment"
                    label={resources.get("OpenEquipment")}
                    onChange={(checked) => changeHandler("openEquipment", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.multimeter.value}
                    id="hot-work-category-two-options__multimeter"
                    label={resources.get("Multimeter")}
                    onChange={(checked) => changeHandler("multimeter", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.megger.value}
                    id="hot-work-category-two-options__megger"
                    label={resources.get("Megger")}
                    onChange={(checked) => changeHandler("megger", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.excavator.value}
                    id="hot-work-category-two-options__excavator"
                    label={resources.get("Excavator")}
                    onChange={(checked) => changeHandler("excavator", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.exCamera.value}
                    id="hot-work-category-two-options__ex-camera"
                    label={resources.get("ExCamera")}
                    onChange={(checked) => changeHandler("exCamera", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <LabelCheckbox
                    checked={formState.GPS.value}
                    id="hot-work-category-two-options__gps"
                    label={resources.get("GPS")}
                    onChange={(checked) => changeHandler("GPS", checked)}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2 d-flex align-items-center m-2">
                <label className="form-label me-1" htmlFor="hot-work-category-two-options__other">
                    {resources.get("Other")}
                </label>
                <input
                    type="text"
                    className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                    id="hot-work-category-two-options__other"
                    value={formState.other.value ?? ""}
                    onChange={(e) => changeHandler("other", e.target.value)}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

export default HotWorkCategoryTwoOptionsModal;
